import React, {useEffect, useState} from 'react';

import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {InstrumentAdapter} from 'instruments/connections/ConnectionConstants';
import {listInstrumentTypes} from 'instruments/connections/ConnectionDAO';
import notifications from 'notifications';
import SelectDropdown, {SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import * as logging from 'util/Logging';

interface AdapterSelectorProps {
  className?: string;
  value: string | null;
  onChange: (adapterId: string | null) => void;
  disabled?: boolean;
  disabledTooltip?: string | null;
}

function AdapterSelector(props: AdapterSelectorProps): JSX.Element {
  // The adapter list is loaded from the instruments service, which can be slow. Render the
  // dropdown immediately (disabled + "Loading...") so the surrounding page shows its shape
  // right away rather than blocking on the request. `null` = still loading.
  const [adapters, setAdapters] = useState<Array<InstrumentAdapter> | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    listInstrumentTypes()
      .then(({instrumentAdapters}) => {
        if (isMounted) {
          setAdapters(instrumentAdapters);
        }
      })
      .catch((error) => {
        // A failure here is unexpected and not something the user can act on: keep the dropdown
        // disabled, surface a generic toast, and report to Sentry.
        if (isMounted) {
          setHasError(true);
          notifications.error(GENERIC_ERROR_MESSAGE);
        }
        logging.error(error);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const isLoading = adapters == null && !hasError;
  const loadedAdapters = adapters ?? [];

  const sortedAdapters = [...loadedAdapters].sort((a, b) => a.displayName.localeCompare(b.displayName));

  const optionDatas = [
    {name: 'None', value: null},
    ...sortedAdapters.map((adapter) => ({
      name: adapter.displayName,
      value: adapter.id,
    })),
  ];

  const selectedAdapter = loadedAdapters.find((adapter) => adapter.id === props.value) ?? null;

  const isDisabled =
    isLoading ||
    hasError ||
    props.disabled ||
    !isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_EDIT});

  // On the happy path, show "Loading..." until the list arrives. Only if loading failed do we
  // fall back to the raw adapter id (if a selection exists) so it isn't blanked out.
  const displayName = isLoading
    ? 'Loading...'
    : hasError
      ? (props.value ?? 'Select...')
      : selectedAdapter == null
        ? 'Select...'
        : selectedAdapter.displayName;

  return (
    <SelectDropdown
      className={props.className}
      disabled={isDisabled}
      displayName={displayName}
      menu={
        <SearchableMenu
          onSelect={props.onChange}
          optionDatas={optionDatas}
          selectedValue={props.value}
          width={Menu.WIDTHS.FULL}
        />
      }
      error={props.disabled ? props.disabledTooltip : undefined}
      errorStyle={props.disabled ? SelectDropdownErrorStyle.TOOLTIP : undefined}
    />
  );
}

export default AdapterSelector;
